import React, { useState, useMemo } from 'react';
import { Alert } from '@billda/design-system';
import { usePendingInvites } from '../hooks/usePendingInvites';
import { useResendInvite } from '../hooks/useResendInvite';
import { useRevokeInvite } from '../hooks/useRevokeInvite';
import { PendingInvitesList } from './organisms/PendingInvitesList';
import { ResendInviteConfirmDialog } from './molecules/ResendInviteConfirmDialog';
import { RevokeInviteConfirmDialog } from './molecules/RevokeInviteConfirmDialog';
import { PendingInviteDto } from '../api/userManagementApi';
import SearchBar from '../../../shared/components/ui/SearchBar';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

const PendingInvitesPanel: React.FC = () => {
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const { data, isLoading, error } = usePendingInvites();

  const [searchText, setSearchText] = useState('');
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<PendingInviteDto | null>(
    null
  );

  const { mutate: resendInvite, isPending: isResending } = useResendInvite();

  const { mutate: revokeInvite, isPending: isRevoking } = useRevokeInvite();

  const filteredInvites = useMemo(() => {
    const invites = data?.invites ?? [];
    if (!searchText.trim()) {
      return invites;
    }
    const searchLower = searchText.toLowerCase();
    return invites.filter(
      invite =>
        invite.invitedEmail.toLowerCase().includes(searchLower) ||
        invite.invitedByUserName.toLowerCase().includes(searchLower)
    );
  }, [data?.invites, searchText]);

  const handleResendClick = (invite: PendingInviteDto) => {
    setSelectedInvite(invite);
    setResendDialogOpen(true);
  };

  const handleRevokeClick = (invite: PendingInviteDto) => {
    setSelectedInvite(invite);
    setRevokeDialogOpen(true);
  };

  const handleResendConfirm = () => {
    if (selectedInvite) {
      resendInvite(selectedInvite.invitationId, {
        onSuccess: () => {
          showSnackbar('Invitation resent successfully', 'success');
        },
        onSettled: () => {
          setResendDialogOpen(false);
          setSelectedInvite(null);
        },
      });
    }
  };

  const handleRevokeConfirm = () => {
    if (selectedInvite) {
      revokeInvite(selectedInvite.invitationId, {
        onSuccess: () => {
          showSnackbar('Invitation revoked successfully', 'success');
        },
        onSettled: () => {
          setRevokeDialogOpen(false);
          setSelectedInvite(null);
        },
      });
    }
  };

  const handleDialogClose = () => {
    setResendDialogOpen(false);
    setRevokeDialogOpen(false);
    setSelectedInvite(null);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  if (error) {
    return (
      <div className="p-6">
        <Alert severity="error">Failed to load pending invitations</Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <p className="type-body-md mb-4 text-content-secondary">
        Pending invitations for your organisation. Resend or revoke invites as
        needed.
      </p>

      <div className="mb-4">
        <SearchBar
          searchText={searchText}
          onSearchChange={setSearchText}
          onClearSearch={handleClearSearch}
          placeholder="Search by email..."
          disabled={isLoading}
          data-testid="invites-search-input"
        />
      </div>

      <PendingInvitesList
        invites={filteredInvites}
        isLoading={isLoading}
        onResend={handleResendClick}
        onRevoke={handleRevokeClick}
        isResending={isResending}
        isRevoking={isRevoking}
      />

      <ResendInviteConfirmDialog
        open={resendDialogOpen}
        email={selectedInvite?.invitedEmail ?? ''}
        onConfirm={handleResendConfirm}
        onCancel={handleDialogClose}
        isLoading={isResending}
      />

      <RevokeInviteConfirmDialog
        open={revokeDialogOpen}
        email={selectedInvite?.invitedEmail ?? ''}
        onConfirm={handleRevokeConfirm}
        onCancel={handleDialogClose}
        isLoading={isRevoking}
      />
    </div>
  );
};

export default PendingInvitesPanel;
